import { z } from "zod";
import { api } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/routers/users.py — staff accounts + active sessions.
   SuperAdmin-only surface (the backend 403s everyone else).
   ────────────────────────────────────────────────────────────────────── */

export const SessionInfo = z.object({
  session_id:     z.number().int(),
  user_id:        z.number().int(),
  username:       z.string(),
  ip:             z.string().nullable().optional(),
  user_agent:     z.string().nullable().optional(),
  issued_at_utc:  z.string().nullable().optional(),
  expires_at_utc: z.string().nullable().optional(),
  status:         z.string().default("active"),   // active | replaced | revoked | expired
  revoked_at_utc: z.string().nullable().optional(),
  replaced_by_session_id: z.number().int().nullable().optional(),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

export const UserRow = z.object({
  user_id:            z.number().int(),
  username:           z.string(),
  email:              z.string().nullable().optional(),
  password_plain:     z.string().nullable().optional(),
  group_id:           z.number().int(),
  group_name:         z.string(),
  is_active:          z.boolean(),
  last_login_utc:     z.string().nullable().optional(),
  last_login_ip:      z.string().nullable().optional(),
  failed_login_count: z.number().int().default(0),
  locked:             z.boolean().default(false),
  session:            SessionInfo.nullable().optional(),
});
export type UserRow = z.infer<typeof UserRow>;

export const UsersResponse = z.object({
  users:             z.array(UserRow),
  assignable_groups: z.record(z.string(), z.string()),
});
export type UsersResponse = z.infer<typeof UsersResponse>;

export interface CreateUserRequest {
  username: string;
  password: string;
  email?: string | null;
  group_id: number;
}

export interface PatchUserRequest {
  username?: string;
  email?: string;          // "" clears it back to empty
  is_active?: boolean;
  group_id?: number;
  new_password?: string;
}

export async function fetchUsers(): Promise<UsersResponse> {
  const r = await api.get("/api/users");
  return UsersResponse.parse(r.data);
}

export async function createUser(req: CreateUserRequest): Promise<UserRow> {
  const r = await api.post("/api/users", req);
  return UserRow.parse(r.data);
}

export async function patchUser(userId: number, req: PatchUserRequest): Promise<void> {
  await api.patch(`/api/users/${userId}`, req);
}

export async function fetchSessions(history = false): Promise<SessionInfo[]> {
  const r = await api.get("/api/users/sessions", { params: { history } });
  return z.object({ sessions: z.array(SessionInfo) }).parse(r.data).sessions;
}

export async function revokeSession(sessionId: number): Promise<void> {
  await api.post(`/api/users/sessions/${sessionId}/revoke`);
}

/** Display labels for the 3 rights — DB group names → what the UI says. */
export const RIGHT_LABEL: Record<string, string> = {
  SuperAdmin: "Super admin",
  Admin:      "Admin",
  Worker:     "User",
};
